import { useState, FormEvent } from 'react';

export default function SignupSection() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const body = new URLSearchParams();
      body.append('email', email.trim());

      const res = await fetch('https://readdy.ai/api/form/d79gegadqn9mib2tr5eg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitted(true);
        setEmail('');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="signup"
      className="relative w-full py-28 md:py-36 px-4 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/2f2450df0b413517e55e536e7c1d4e2b.jpg"
          alt="Lake Claiborne aerial view"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-slate-900/85" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-2xl mx-auto text-center">
        <span className="inline-block bg-teal-500 text-white text-xs font-bold tracking-widest uppercase rounded-full px-4 py-1.5 mb-8">
          Directory Launching Soon
        </span>

        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
          Be the First to Explore<br />
          <span className="text-teal-400 italic">Our Property Listings</span>
        </h2>

        <p className="text-white/75 text-lg leading-relaxed mb-12 max-w-lg mx-auto">
          Sign up and we'll notify you the moment our directory goes live — showcasing the best short-term rental properties on Lake Claiborne, all in one place.
        </p>

        {submitted ? (
          <div className="bg-teal-500/20 border border-teal-400/40 rounded-2xl px-8 py-10 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-teal-500 text-white mx-auto mb-4">
              <i className="ri-check-line text-2xl" />
            </div>
            <h3 className="text-white text-xl font-semibold mb-2">You're on the list!</h3>
            <p className="text-white/70 text-base">We'll reach out as soon as properties are available. See you on the lake!</p>
          </div>
        ) : (
          <>
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row items-stretch bg-white rounded-full overflow-hidden max-w-xl mx-auto"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="flex-1 px-6 py-4 text-slate-700 text-base outline-none bg-transparent placeholder-slate-400"
                required
              />
              <button
                type="submit"
                disabled={loading}
                className="whitespace-nowrap bg-teal-500 hover:bg-teal-400 text-white font-semibold text-base px-8 py-4 transition-colors duration-200 cursor-pointer disabled:opacity-60 sm:rounded-r-full"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin" /> Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Notify Me <i className="ri-arrow-right-line" />
                  </span>
                )}
              </button>
            </form>

            {error && (
              <p className="text-red-400 text-sm mt-3">{error}</p>
            )}

            <p className="text-white/40 text-sm mt-5">
              We respect your privacy. No spam, ever. Unsubscribe anytime.
            </p>
          </>
        )}

        {/* Preview circles */}
        <div className="flex items-center justify-center mt-14 gap-1">
          {[
            { seq: 'prev1', label: 'Cabin 1' },
            { seq: 'prev2', label: 'Cabin 2' },
            { seq: 'prev3', label: 'Cabin 3' },
            { seq: 'prev4', label: 'Cabin 4' },
            { seq: 'prev5', label: 'Cabin 5' },
          ].map(({ seq, label }, i) => (
            <div
              key={seq}
              className="w-12 h-12 rounded-full border-2 border-white/60 overflow-hidden -ml-2 first:ml-0"
              style={{ zIndex: 5 - i }}
            >
              <img
                src={`https://readdy.ai/api/search-image?query=cozy%20lakefront%20vacation%20rental%20cabin%20exterior%2C%20Lake%20Claiborne%20Louisiana%2C%20wooden%20dock%2C%20pine%20trees%2C%20warm%20inviting%2C%20property%20photo&width=80&height=80&seq=${seq}&orientation=squarish`}
                alt={label}
                className="w-full h-full object-cover object-top"
              />
            </div>
          ))}
          <span className="text-white/60 text-sm ml-4">Premium properties coming soon</span>
        </div>
      </div>
    </section>
  );
}
